<template>
	<div class="verification-step">
		<h3 class="step-title">{{ copy.title }}</h3>
		<p class="step-desc">{{ copy.sentTo }} {{ contactInfo }}</p>

		<div class="code-list">
			<input
				v-for="(_, index) in codes"
				:key="index"
				:ref="el => setInputRef(el, index)"
				v-model="codes[index]"
				type="text"
				inputmode="numeric"
				maxlength="1"
				class="code-item"
				@input="handleInput(index, $event)"
				@keydown="handleKeydown(index, $event)"
				@paste="handlePaste"
			/>
		</div>

		<div class="resend-text">
			<span v-if="countdown > 0"
				>{{ copy.resendPrefix }}{{ countdown }}{{ copy.resendSuffix }}</span
			>
			<button v-else type="button" @click="handleResend">{{ copy.resend }}</button>
		</div>

		<button class="next-btn" :class="{ enabled: canSubmit }" type="button" @click="handleSubmit">
			{{ copy.next }}
		</button>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
	contactInfo: {
		type: String,
		required: true,
	},
	/** integrated: 一步式（新登录）| step: 多步式（企业中心） */
	variant: {
		type: String,
		default: "integrated",
		validator: v => ["integrated", "step"].includes(v),
	},
	/** UI 語言：`zh-TW`（預設）| `en` */
	locale: {
		type: String,
		default: "zh-TW",
	},
});

const copy = computed(() =>
	props.locale === "en"
		? {
				title: "Enter verification code",
				sentTo: "We sent a code to",
				resendPrefix: "Resend code in ",
				resendSuffix: "s",
				resend: "Resend code",
				next: "Next",
			}
		: {
				title: "輸入驗證碼",
				sentTo: "我們已發送驗證碼至",
				resendPrefix: "重新發送驗證碼（",
				resendSuffix: "秒）",
				resend: "重新發送驗證碼",
				next: props.variant === "step" ? "下一步" : "登入 / 註冊",
			}
);

const emit = defineEmits(["complete", "resend"]);

const codes = ref(["", "", "", "", "", ""]);
const inputRefs = ref([]);
const countdown = ref(59);

let timer = null;

const canSubmit = computed(() => codes.value.join("").length === 6);

const setInputRef = (el, index) => {
	if (el) {
		inputRefs.value[index] = el;
	}
};

const focusInput = index => {
	const target = inputRefs.value[index];
	target?.focus();
	target?.select?.();
};

const startCountdown = () => {
	if (timer) {
		clearInterval(timer);
	}
	countdown.value = 59;
	timer = setInterval(() => {
		if (countdown.value <= 0) {
			clearInterval(timer);
			timer = null;
			return;
		}
		countdown.value -= 1;
	}, 1000);
};

const resetInputs = async () => {
	codes.value = ["", "", "", "", "", ""];
	await nextTick();
	focusInput(0);
};

const handleInput = (index, event) => {
	const value = event.target.value.replace(/\D/g, "");
	codes.value[index] = value;

	if (value && index < 5) {
		focusInput(index + 1);
	}
};

const handleKeydown = (index, event) => {
	if (event.key === "Backspace" && !codes.value[index] && index > 0) {
		focusInput(index - 1);
	}
};

const handlePaste = event => {
	event.preventDefault();
	const pastedValue = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
	const nextCodes = ["", "", "", "", "", ""];

	for (let i = 0; i < pastedValue.length; i += 1) {
		nextCodes[i] = pastedValue[i];
	}

	codes.value = nextCodes;
	focusInput(Math.min(pastedValue.length, 5));
};

const handleResend = async () => {
	emit("resend");
	startCountdown();
	await resetInputs();
};

const handleSubmit = () => {
	if (!canSubmit.value) {
		return;
	}

	emit("complete", codes.value.join(""));
};

onMounted(async () => {
	startCountdown();
	await nextTick();
	focusInput(0);
});

onBeforeUnmount(() => {
	if (timer) {
		clearInterval(timer);
	}
});
</script>

<style lang="scss" scoped>
.verification-step {
	.step-title {
		margin: 0 0 12px;
		font-size: 24px;
		font-weight: 700;
		line-height: 1.35;
		color: #333333;
	}

	.step-desc {
		margin: 0 0 26px;
		font-size: 16px;
		line-height: 1.5;
		color: #7f7f7f;
		word-break: break-all;
	}

	.code-list {
		display: grid;
		grid-template-columns: repeat(6, 1fr);
		gap: 10px;
		margin-bottom: 18px;
	}

	.code-item {
		width: 100%;
		height: 52px;
		border: 1px solid #dddddd;
		border-radius: 10px;
		outline: none;
		background: #ffffff;
		font-size: 24px;
		font-weight: 600;
		text-align: center;
		color: #333333;

		&:focus {
			border-color: #111111;
		}
	}

	.resend-text {
		margin-bottom: 22px;
		font-size: 16px;
		line-height: 1.5;
		color: #7f7f7f;

		button {
			padding: 0;
			background: transparent;
			border: 0;
			font: inherit;
			color: #4a7dff;
			cursor: pointer;
		}
	}

	.next-btn {
		width: 100%;
		height: 46px;
		border: 0;
		border-radius: 10px;
		background: #d9d9d9;
		font-size: 18px;
		font-weight: 600;
		color: #ffffff;
		cursor: not-allowed;
		transition: background-color 0.2s ease;

		&.enabled {
			background: #111111;
			cursor: pointer;
		}
	}
}
</style>
